import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuItems, setMenuItems] = useState([
    { id: 'home', label: 'Home', url: '/', children: [] },
    { id: 'services', label: 'Services', url: '/services', children: [] },
    { id: 'training', label: 'Training', url: '/training', children: [] },
    { id: 'products', label: 'Products', url: '/products', children: [] },
    { id: 'about', label: 'About', url: '/about', children: [] },
    { id: 'blog', label: 'Blog', url: '/blog', children: [] },
    { id: 'contact', label: 'Contact', url: '/contact', children: [] }
  ]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Try to load from API, fallback to default
  useEffect(() => {
    axios.get(`${API_URL}/menu`)
      .then(response => {
        if (response.data && response.data.length > 0) {
          setMenuItems(response.data);
        }
      })
      .catch(() => {
        // Use default menu - already set
        console.log('Using default menu');
      });
  }, []);

  const topLevelItems = menuItems.filter(item => !item.parentId);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled || isOpen ? 'bg-navy/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold">
            <span className="text-cyan">Aamir</span>
            <span className="text-white">Saba</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {topLevelItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              return (
                <div key={item.id} className="relative group">
                  {hasChildren ? (
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                      className="text-sm font-medium text-gray-300 hover:text-cyan transition flex items-center gap-1"
                    >
                      {item.label} <FaChevronDown size={10} />
                    </button>
                  ) : (
                    <Link
                      to={item.url}
                      className={`text-sm font-medium transition-colors hover:text-cyan ${
                        location.pathname === item.url ? 'text-cyan' : 'text-gray-300'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                  
                  {hasChildren && (
                    <div className={`absolute top-full left-0 mt-2 bg-darkNavy rounded-lg border border-gray-700 shadow-xl min-w-[200px] py-2 transition-all duration-200 ${
                      openDropdown === item.id ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
                    }`}>
                      {item.children.map((child, index) => (
                        <Link
                          key={index}
                          to={child.url}
                          className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan hover:bg-cyan/10 transition"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden md:block">
            <Link to="/contact" className="px-6 py-2 bg-cyan text-navy rounded-full font-semibold hover:bg-cyan/80 transition">
              Get Started
            </Link>
          </div>

          <button className="md:hidden text-2xl text-white z-50" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden mt-4 py-6 px-4 bg-darkNavy rounded-xl border border-gray-700 shadow-2xl max-h-[80vh] overflow-y-auto">
            {topLevelItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              return (
                <div key={item.id}>
                  {hasChildren ? (
                    <>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                        className="flex items-center justify-between w-full py-3 text-gray-300 hover:text-cyan transition text-base font-medium border-b border-gray-800"
                      >
                        {item.label} <FaChevronDown className={`transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === item.id && (
                        <div className="pl-4 space-y-1">
                          {item.children.map((child, index) => (
                            <Link
                              key={index}
                              to={child.url}
                              className="block py-2 text-sm text-gray-400 hover:text-cyan transition"
                              onClick={() => setIsOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.url}
                      className="block py-3 text-gray-300 hover:text-cyan transition text-base font-medium border-b border-gray-800 last:border-0"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              );
            })}
            <Link 
              to="/contact" 
              className="block mt-4 px-6 py-3 bg-cyan text-navy rounded-full text-center font-semibold hover:bg-cyan/80 transition"
              onClick={() => setIsOpen(false)}
            >
              Get Started
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
